/**
 * Custom Shorts — infinite centered carousel, muted autoplay on active slide.
 * Loops via YT API. YouTube chrome hidden; mute + click-to-play/pause overlay.
 */
(function () {
  'use strict';

  var oembedCache = {};
  var YT_PLAYING = 1;
  var YT_PAUSED = 2;
  var YT_ENDED = 0;

  var MUTE_ICON_OFF =
    '<svg class="custom-shorts__mute-icon custom-shorts__mute-icon--off" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M11 5L6 9H3v6h3l5 4V5z" fill="currentColor"/>' +
    '<path d="M16.5 8.5l5 5M21.5 8.5l-5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
    '</svg>';

  var MUTE_ICON_ON =
    '<svg class="custom-shorts__mute-icon custom-shorts__mute-icon--on" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M11 5L6 9H3v6h3l5 4V5z" fill="currentColor"/>' +
    '<path d="M15.5 8.5a5 5 0 010 7M18.5 6a8.5 8.5 0 010 12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
    '</svg>';

  function CustomShorts(root) {
    this.root = root;
    this.track = root.querySelector('[data-cs-track]');
    this.status = root.querySelector('[data-cs-status]');
    this.prevBtns = root.querySelectorAll('[data-cs-prev]');
    this.nextBtns = root.querySelectorAll('[data-cs-next]');
    this.items = parseItems(readItemsJson(root));
    this.shorts = [];
    this.slides = [];
    this.realCount = 0;
    this.loopEnabled = false;
    this.activeIndex = 0;
    this.playingSlide = null;
    this.outgoingSlide = null;
    this.isMuted = true;
    this.isUserPaused = false;
    this.isInView = false;
    this.isPageVisible = !document.hidden;
    this.isVisibilityPaused = false;
    this.isAnimating = false;
    this._holdActiveUntil = 0;
    this._animToken = 0;
    this._wActive = 0;
    this._wInactive = 0;
    this._lockedTrackMinHeight = 0;
    this._raf = null;
    this._scrollRaf = null;
    this._scrollLockTimer = null;
    this._handoffTimer = null;
    this._handoffSafetyTimer = null;
    this._ytPlayerId = 0;
    this._io = null;
    this._onScroll = this.onScroll.bind(this);
    this._onResize = this.onResize.bind(this);
    this._onTrackClick = this.onTrackClick.bind(this);
    this._onMessage = this.onYouTubeMessage.bind(this);
    this._onPageVisibility = this.onPageVisibilityChange.bind(this);

    this._onTouchStart = this.onTouchStart.bind(this);
    this._onTouchMove = this.onTouchMove.bind(this);
    this._onTouchEnd = this.onTouchEnd.bind(this);
    this._touch = null;

    this.bind();
    this.load();
  }

  function readItemsJson(root) {
    var node = root.querySelector('[data-cs-items-json]');
    if (!node) return '[]';
    return (node.textContent || '[]').trim() || '[]';
  }

  function parseItems(raw) {
    try {
      var list = JSON.parse(raw);
      if (!Array.isArray(list)) return [];
      return list
        .map(function (item) {
          return {
            title: String((item && item.title) || '').trim(),
            url: String((item && item.url) || '').trim()
          };
        })
        .filter(function (item) {
          return item.url || extractVideoId(item.title);
        });
    } catch (e) {
      return [];
    }
  }

  function extractVideoId(value) {
    var str = String(value || '').trim();
    if (!str) return '';
    if (/^[\w-]{11}$/.test(str)) return str;

    var patterns = [
      /youtube\.com\/shorts\/([\w-]{11})/i,
      /youtu\.be\/([\w-]{11})/i,
      /youtube\.com\/watch\?[^#]*v=([\w-]{11})/i,
      /youtube\.com\/embed\/([\w-]{11})/i
    ];

    for (var i = 0; i < patterns.length; i++) {
      var match = str.match(patterns[i]);
      if (match && match[1]) return match[1];
    }
    return '';
  }

  function shortsUrl(videoId) {
    return 'https://www.youtube.com/shorts/' + videoId;
  }

  function thumbnailCandidates(videoId) {
    return [
      'https://i.ytimg.com/vi/' + videoId + '/oar2.jpg',
      'https://i.ytimg.com/vi/' + videoId + '/oardefault.jpg',
      'https://i.ytimg.com/vi/' + videoId + '/maxresdefault.jpg',
      'https://i.ytimg.com/vi/' + videoId + '/sddefault.jpg',
      'https://i.ytimg.com/vi/' + videoId + '/hqdefault.jpg'
    ];
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function fetchJson(url) {
    return fetch(url, { credentials: 'omit' }).then(function (res) {
      if (!res.ok) throw new Error('Request failed');
      return res.json();
    });
  }

  function fetchOEmbedMeta(watchUrl) {
    if (oembedCache[watchUrl]) return oembedCache[watchUrl];

    var request = fetchJson(
      'https://www.youtube.com/oembed?url=' + encodeURIComponent(watchUrl) + '&format=json'
    )
      .catch(function () {
        return fetchJson('https://noembed.com/embed?url=' + encodeURIComponent(watchUrl));
      })
      .then(function (data) {
        return {
          title: (data && data.title) || '',
          thumbnail: (data && (data.thumbnail_url || data.thumbnail)) || ''
        };
      })
      .catch(function () {
        return { title: '', thumbnail: '' };
      });

    oembedCache[watchUrl] = request;
    return request;
  }

  function postYT(iframe, payload) {
    if (!iframe || !iframe.contentWindow) return;
    try {
      iframe.contentWindow.postMessage(JSON.stringify(payload), '*');
    } catch (e) {}
  }

  function postYTCommand(iframe, func, args) {
    postYT(iframe, { event: 'command', func: func, args: args || [] });
  }

  function bindHdThumbnail(img, videoId, preferredUrl) {
    var candidates = thumbnailCandidates(videoId);
    if (preferredUrl) candidates.push(preferredUrl);

    var unique = [];
    var seen = {};
    candidates.forEach(function (url) {
      if (!url || seen[url]) return;
      seen[url] = true;
      unique.push(url);
    });

    var index = 0;

    function tryNext() {
      if (index >= unique.length) return;
      var url = unique[index++];
      img.onload = function () {
        if (img.naturalWidth && img.naturalWidth < 200 && index < unique.length) {
          tryNext();
        }
      };
      img.onerror = function () {
        tryNext();
      };
      img.src = url;
    }

    tryNext();
  }

  CustomShorts.prototype.bind = function () {
    var self = this;

    this.prevBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.step(-1);
      });
    });

    this.nextBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.step(1);
      });
    });

    if (this.track) {
      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      this.track.addEventListener('click', this._onTrackClick);
      if ('onscrollend' in window) {
        this.track.addEventListener('scrollend', function () {
          // Ignore while our eased RAF scroll is still running.
          if (self._scrollRaf) return;
          if (self._touch) return;
          self.finishProgrammaticScroll();
        });
      }

      if (this.isTouchDevice()) {
        this.root.classList.add('is-touch');
        this.track.addEventListener('touchstart', this._onTouchStart, { passive: true });
        this.track.addEventListener('touchmove', this._onTouchMove, { passive: false });
        this.track.addEventListener('touchend', this._onTouchEnd, { passive: true });
        this.track.addEventListener('touchcancel', this._onTouchEnd, { passive: true });
      }

      if (this.needsChromeCrop()) {
        this.root.classList.add('cs-chrome-crop');
      }
    }

    window.addEventListener('message', this._onMessage);
    window.addEventListener('resize', this._onResize, { passive: true });
    document.addEventListener('visibilitychange', this._onPageVisibility);

    if (typeof IntersectionObserver === 'function') {
      this._io = new IntersectionObserver(
        function (entries) {
          var entry = entries && entries[0];
          if (!entry) return;
          // Play while any meaningful part of the section is on screen.
          self.onSectionVisibilityChange(entry.isIntersecting && entry.intersectionRatio >= 0.05);
        },
        { root: null, threshold: [0, 0.05, 0.15, 0.3, 0.6], rootMargin: '0px' }
      );
      this._io.observe(this.root);
    } else {
      this.isInView = true;
    }
  };

  CustomShorts.prototype.refreshInView = function () {
    if (!this.root) return;
    var rect = this.root.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    if (!vh) {
      this.isInView = true;
      return;
    }
    var visible = rect.bottom > 0 && rect.top < vh;
    var height = Math.max(rect.height, 1);
    var visibleHeight = Math.min(rect.bottom, vh) - Math.max(rect.top, 0);
    var ratio = Math.max(0, visibleHeight) / height;
    var inView = visible && ratio >= 0.05;
    if (inView !== this.isInView) {
      this.onSectionVisibilityChange(inView);
    } else {
      this.isInView = inView;
    }
  };

  CustomShorts.prototype.canPlay = function () {
    return this.isInView && this.isPageVisible;
  };

  CustomShorts.prototype.onSectionVisibilityChange = function (inView) {
    if (inView === this.isInView) return;
    this.isInView = inView;
    this.syncPlaybackToVisibility();
  };

  CustomShorts.prototype.onPageVisibilityChange = function () {
    this.isPageVisible = !document.hidden;
    this.syncPlaybackToVisibility();
  };

  CustomShorts.prototype.syncPlaybackToVisibility = function () {
    if (this.canPlay()) {
      this.ensureActivePlaying();
    } else {
      this.pauseActiveVideo();
    }
  };

  CustomShorts.prototype.pauseActiveVideo = function () {
    if (!this.playingSlide) return;
    var iframe = this.playingSlide.querySelector('[data-cs-iframe]');
    if (!iframe) return;
    this.isVisibilityPaused = true;
    postYTCommand(iframe, 'pauseVideo');
  };

  CustomShorts.prototype.resumeActiveVideo = function () {
    if (!this.playingSlide || !this.canPlay() || this.isUserPaused) return;
    var iframe = this.playingSlide.querySelector('[data-cs-iframe]');
    if (!iframe) return;
    this.isVisibilityPaused = false;
    postYTCommand(iframe, 'playVideo');
    this.applyMuteState(iframe);
  };

  CustomShorts.prototype.applyMuteState = function (iframe) {
    if (!iframe) return;
    if (this.isMuted) {
      postYTCommand(iframe, 'mute');
    } else {
      postYTCommand(iframe, 'unMute');
      postYTCommand(iframe, 'setVolume', [100]);
    }
  };

  CustomShorts.prototype.syncMuteButton = function (slide) {
    var target = slide || this.playingSlide;
    if (!target) return;
    var muteBtn = target.querySelector('[data-cs-mute]');
    if (!muteBtn) return;
    muteBtn.setAttribute('aria-pressed', this.isMuted ? 'true' : 'false');
    muteBtn.setAttribute('aria-label', this.isMuted ? 'Unmute' : 'Mute');
  };

  CustomShorts.prototype.destroy = function () {
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('message', this._onMessage);
    document.removeEventListener('visibilitychange', this._onPageVisibility);
    if (this._io) {
      this._io.disconnect();
      this._io = null;
    }
    if (this.track) {
      this.track.removeEventListener('scroll', this._onScroll);
      this.track.removeEventListener('click', this._onTrackClick);
      this.track.removeEventListener('touchstart', this._onTouchStart);
      this.track.removeEventListener('touchmove', this._onTouchMove);
      this.track.removeEventListener('touchend', this._onTouchEnd);
      this.track.removeEventListener('touchcancel', this._onTouchEnd);
    }
    this.stopPlaying();
    if (this._raf) cancelAnimationFrame(this._raf);
    if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
    if (this._scrollLockTimer) clearTimeout(this._scrollLockTimer);
    if (this._handoffTimer) clearTimeout(this._handoffTimer);
    if (this._handoffSafetyTimer) clearTimeout(this._handoffSafetyTimer);
    this.clearSlideSizeOverrides();
  };

  CustomShorts.prototype.onTrackClick = function (event) {
    var muteBtn = event.target.closest('[data-cs-mute]');
    if (muteBtn) {
      event.preventDefault();
      event.stopPropagation();
      this.toggleMute(muteBtn);
      return;
    }

    var chrome = event.target.closest('.custom-shorts__yt-chrome');
    if (chrome) {
      event.preventDefault();
      event.stopPropagation();
      this.togglePlayPause();
      return;
    }

    var slide = event.target.closest('[data-cs-slide]');
    if (!slide) return;

    var index = parseInt(slide.getAttribute('data-index'), 10);
    if (isNaN(index) || index === this.activeIndex) return;

    event.preventDefault();
    this.goTo(index);
  };

  CustomShorts.prototype.togglePlayPause = function () {
    if (!this.playingSlide || !this.canPlay()) return;
    var iframe = this.playingSlide.querySelector('[data-cs-iframe]');
    if (!iframe) return;

    if (this.isUserPaused) {
      this.isUserPaused = false;
      this.isVisibilityPaused = false;
      postYTCommand(iframe, 'playVideo');
      this.applyMuteState(iframe);
    } else {
      this.isUserPaused = true;
      postYTCommand(iframe, 'pauseVideo');
    }
  };

  CustomShorts.prototype.setStatus = function (message, isError) {
    if (!this.status) return;
    if (!message) {
      this.status.hidden = true;
      this.status.textContent = '';
      this.status.classList.remove('custom-shorts__status--error');
      return;
    }
    this.status.hidden = false;
    this.status.textContent = message;
    this.status.classList.toggle('custom-shorts__status--error', !!isError);
  };

  CustomShorts.prototype.setLoading = function (loading) {
    this.root.classList.toggle('is-loading', !!loading);
  };

  CustomShorts.prototype.resolveItem = function (item) {
    var videoId = extractVideoId(item.url) || extractVideoId(item.title);
    if (!videoId) return Promise.resolve(null);

    var watchUrl = 'https://www.youtube.com/watch?v=' + videoId;
    var providedTitle = item.title && !extractVideoId(item.title) ? item.title : '';

    return fetchOEmbedMeta(watchUrl).then(function (meta) {
      return {
        id: videoId,
        title: providedTitle || meta.title || 'YouTube Short',
        thumbnail: meta.thumbnail || thumbnailCandidates(videoId)[0],
        url: shortsUrl(videoId)
      };
    });
  };

  CustomShorts.prototype.load = function () {
    var self = this;

    if (!this.items.length) {
      this.setStatus('Add Shorts pair blocks with a YouTube Shorts URL.', true);
      this.renderSkeletons(5);
      return;
    }

    this.setLoading(true);
    this.setStatus('');
    this.renderSkeletons(Math.min(this.items.length, 7));

    Promise.all(
      this.items.map(function (item) {
        return self.resolveItem(item);
      })
    )
      .then(function (resolved) {
        var shorts = resolved.filter(Boolean);
        if (!shorts.length) {
          throw new Error('No valid YouTube Shorts URLs were found in the blocks.');
        }
        self.renderSlides(shorts);
        self.setLoading(false);
        self.setStatus('');
        requestAnimationFrame(function () {
          self.centerInitial();
        });
      })
      .catch(function (err) {
        self.setLoading(false);
        self.renderSlides([]);
        self.setStatus(err && err.message ? err.message : 'Unable to load Shorts.', true);
      });
  };

  CustomShorts.prototype.renderSkeletons = function (count) {
    if (!this.track) return;
    var html = '';
    for (var i = 0; i < count; i++) {
      html +=
        '<div class="custom-shorts__skeleton" aria-hidden="true">' +
        '<div class="custom-shorts__skeleton-media"></div></div>';
    }
    this.track.innerHTML = html;
    this.slides = [];
    this.realCount = 0;
    this.loopEnabled = false;
    this.updateNavState();
  };

  CustomShorts.prototype.buildSlideHtml = function (item, domIndex, realIndex) {
    var muteBtn =
      '<button type="button" class="custom-shorts__mute" data-cs-mute aria-pressed="true" aria-label="Unmute">' +
      MUTE_ICON_OFF +
      MUTE_ICON_ON +
      '</button>';

    var ytChrome =
      '<div class="custom-shorts__yt-chrome" aria-hidden="true">' +
      '<span class="custom-shorts__yt-chrome-top"></span>' +
      '<span class="custom-shorts__yt-chrome-side custom-shorts__yt-chrome-side--left"></span>' +
      '<span class="custom-shorts__yt-chrome-side custom-shorts__yt-chrome-side--right"></span>' +
      '<span class="custom-shorts__yt-chrome-bottom"></span>' +
      '</div>';

    return (
      '<article class="custom-shorts__slide" data-cs-slide data-index="' +
      domIndex +
      '" data-real-index="' +
      realIndex +
      '" data-video-id="' +
      escapeHtml(item.id) +
      '">' +
      '<div class="custom-shorts__slide-card">' +
      '<div class="custom-shorts__slide-media" data-cs-media>' +
      '<img alt="' +
      escapeHtml(item.title) +
      '" width="720" height="1280" decoding="async" loading="eager" data-cs-thumb data-preferred-thumb="' +
      escapeHtml(item.thumbnail) +
      '">' +
      ytChrome +
      muteBtn +
      '</div>' +
      '<p class="custom-shorts__slide-title">' +
      escapeHtml(item.title) +
      '</p>' +
      '</div>' +
      '</article>'
    );
  };

  CustomShorts.prototype.renderSlides = function (shorts) {
    if (!this.track) return;
    this.stopPlaying();

    if (!shorts.length) {
      this.track.innerHTML = '';
      this.slides = [];
      this.shorts = [];
      this.realCount = 0;
      this.loopEnabled = false;
      this.updateNavState();
      return;
    }

    this.shorts = shorts;
    this.realCount = shorts.length;
    this.loopEnabled = shorts.length > 1;

    var copies = this.loopEnabled ? 3 : 1;
    var html = '';
    var domIndex = 0;

    for (var copy = 0; copy < copies; copy++) {
      for (var i = 0; i < shorts.length; i++) {
        html += this.buildSlideHtml(shorts[i], domIndex, i);
        domIndex += 1;
      }
    }

    this.track.innerHTML = html;
    this.track.classList.toggle('is-infinite', this.loopEnabled);
    this.slides = Array.prototype.slice.call(this.track.querySelectorAll('[data-cs-slide]'));

    this.slides.forEach(function (slide) {
      var img = slide.querySelector('[data-cs-thumb]');
      var videoId = slide.getAttribute('data-video-id');
      if (!img || !videoId) return;
      bindHdThumbnail(img, videoId, img.getAttribute('data-preferred-thumb'));
    });

    this.activeIndex = this.loopEnabled
      ? this.realCount + Math.min(Math.floor(this.realCount / 2), this.realCount - 1)
      : Math.min(Math.floor(this.realCount / 2), this.realCount - 1);

    this.applyActiveClass(this.activeIndex);
    this.updateNavState();
  };

  CustomShorts.prototype.embedUrl = function (videoId, muted, playerId) {
    var origin = '';
    try {
      origin = window.location.origin || '';
    } catch (e) {}

    // Hide YouTube chrome in the URL; Firefox/Safari still draw some UI — CSS
    // scales the iframe so overflow:hidden on media crops that chrome away.
    return (
      'https://www.youtube.com/embed/' +
      encodeURIComponent(videoId) +
      '?autoplay=1&playsinline=1&rel=0&modestbranding=1&controls=0&fs=0&iv_load_policy=3&cc_load_policy=0&disablekb=1&enablejsapi=1&mute=' +
      (muted ? '1' : '0') +
      '&playerapiid=' +
      encodeURIComponent(playerId) +
      (origin ? '&origin=' + encodeURIComponent(origin) : '') +
      (origin ? '&widget_referrer=' + encodeURIComponent(origin) : '')
    );
  };

  CustomShorts.prototype.playSlide = function (slide, options) {
    if (!slide || !this.canPlay()) return;
    options = options || {};

    if (this.playingSlide === slide && slide.classList.contains('is-playing')) {
      this.resumeActiveVideo();
      return;
    }

    var videoId = slide.getAttribute('data-video-id');
    var media = slide.querySelector('[data-cs-media]');
    if (!videoId || !media) return;

    // Keep the outgoing video alive until the incoming one is actually playing.
    if (this.playingSlide && this.playingSlide !== slide) {
      if (options.keepOutgoing !== false) {
        if (this.outgoingSlide && this.outgoingSlide !== this.playingSlide) {
          this.teardownSlide(this.outgoingSlide);
        }
        this.outgoingSlide = this.playingSlide;
        this.outgoingSlide.classList.add('is-outgoing');
      } else {
        this.teardownSlide(this.playingSlide);
        if (this.outgoingSlide) {
          this.teardownSlide(this.outgoingSlide);
          this.outgoingSlide = null;
        }
      }
    }

    // Clear any stale player already on the target slide.
    var existing = media.querySelector('[data-cs-iframe]');
    if (existing) {
      try {
        existing.src = 'about:blank';
      } catch (e) {}
      existing.remove();
    }
    slide.classList.remove('is-ready', 'is-outgoing');

    this._ytPlayerId += 1;
    var playerId = 'cs-yt-' + this._ytPlayerId;

    var iframe = document.createElement('iframe');
    iframe.id = playerId;
    // Always start muted in the URL for browser autoplay policy, then apply saved mute state.
    iframe.src = this.embedUrl(videoId, true, playerId);
    iframe.title = (slide.querySelector('.custom-shorts__slide-title') || {}).textContent || 'YouTube Short';
    iframe.allow =
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('data-cs-iframe', '');
    iframe.setAttribute('data-cs-player-id', playerId);
    iframe.setAttribute('tabindex', '-1');

    media.appendChild(iframe);
    slide.classList.add('is-playing');
    this.playingSlide = slide;
    this.isVisibilityPaused = false;
    this.isUserPaused = false;
    this.syncMuteButton(slide);
    if (this.outgoingSlide) this.scheduleHandoffSafety(slide);

    var self = this;
    iframe.addEventListener('load', function () {
      if (self.playingSlide !== slide) return;
      self.handshakeYouTube(iframe, playerId);
      // Reveal iframe as soon as it loads so playback feels immediate.
      self.markSlideReady(slide);
      if (!self.canPlay()) {
        self.pauseActiveVideo();
        return;
      }
      postYTCommand(iframe, 'playVideo');
      self.applyMuteState(iframe);
    });
  };

  CustomShorts.prototype.handshakeYouTube = function (iframe, playerId) {
    postYT(iframe, { event: 'listening', id: playerId });
    postYTCommand(iframe, 'addEventListener', ['onStateChange']);
    postYTCommand(iframe, 'addEventListener', ['onReady']);
  };

  CustomShorts.prototype.markSlideReady = function (slide) {
    if (!slide) return;
    slide.classList.add('is-ready');
    if (this._handoffSafetyTimer) {
      clearTimeout(this._handoffSafetyTimer);
      this._handoffSafetyTimer = null;
    }
    this.finishOutgoingHandoff();
  };

  CustomShorts.prototype.finishOutgoingHandoff = function () {
    if (!this.outgoingSlide) return;
    if (this.outgoingSlide === this.playingSlide) {
      this.outgoingSlide.classList.remove('is-outgoing');
      this.outgoingSlide = null;
      return;
    }
    this.teardownSlide(this.outgoingSlide);
    this.outgoingSlide = null;
  };

  CustomShorts.prototype.scheduleHandoffSafety = function (slide) {
    var self = this;
    if (this._handoffSafetyTimer) clearTimeout(this._handoffSafetyTimer);
    // If YouTube is slow to report PLAYING, still release the outgoing slide.
    this._handoffSafetyTimer = setTimeout(function () {
      self._handoffSafetyTimer = null;
      if (self.playingSlide === slide) self.markSlideReady(slide);
    }, 900);
  };

  CustomShorts.prototype.teardownSlide = function (slide) {
    if (!slide) return;
    var iframe = slide.querySelector('[data-cs-iframe]');
    if (iframe) {
      try {
        iframe.src = 'about:blank';
      } catch (e) {}
      iframe.remove();
    }
    slide.classList.remove('is-playing', 'is-ready', 'is-outgoing');
    if (this.playingSlide === slide) this.playingSlide = null;
    if (this.outgoingSlide === slide) this.outgoingSlide = null;
  };

  CustomShorts.prototype.onYouTubeMessage = function (event) {
    if (!this.playingSlide || !event || event.data == null) return;

    var data = event.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (e) {
        return;
      }
    }
    if (!data || typeof data !== 'object') return;

    var iframe = this.playingSlide.querySelector('[data-cs-iframe]');
    if (!iframe) return;

    var playerId = iframe.getAttribute('data-cs-player-id');
    if (data.id && playerId && String(data.id) !== String(playerId)) return;

    if (data.event === 'onReady' || data.event === 'initialDelivery') {
      this.handshakeYouTube(iframe, playerId);
      this.markSlideReady(this.playingSlide);
      if (!this.canPlay()) {
        this.pauseActiveVideo();
        return;
      }
      postYTCommand(iframe, 'playVideo');
      this.applyMuteState(iframe);
      return;
    }

    var state = null;
    if (data.event === 'onStateChange' && data.info != null) {
      state = typeof data.info === 'object' ? data.info : data.info;
    } else if (data.event === 'infoDelivery' && data.info && data.info.playerState != null) {
      state = data.info.playerState;
    }

    if (state === YT_ENDED) {
      if (!this.canPlay()) return;
      this.isUserPaused = false;
      postYTCommand(iframe, 'seekTo', [0, true]);
      postYTCommand(iframe, 'playVideo');
      this.applyMuteState(iframe);
    } else if (state === YT_PAUSED) {
      // Only treat as user pause after the video has actually started (YT often
      // emits PAUSED during initial load — that must not block page-load autoplay).
      if (
        this.canPlay() &&
        !this.isVisibilityPaused &&
        this.playingSlide &&
        this.playingSlide.classList.contains('is-ready')
      ) {
        this.isUserPaused = true;
      }
    } else if (state === YT_PLAYING) {
      if (!this.canPlay() || this.isVisibilityPaused) {
        postYTCommand(iframe, 'pauseVideo');
        return;
      }
      this.isUserPaused = false;
      this.markSlideReady(this.playingSlide);
      this.applyMuteState(iframe);
    }
  };

  CustomShorts.prototype.ensureActivePlaying = function (options) {
    if (!this.canPlay()) return;
    var slide = this.slides[this.activeIndex];
    if (!slide) return;
    if (this.playingSlide === slide && slide.classList.contains('is-playing')) {
      if (!this.isUserPaused) this.resumeActiveVideo();
      return;
    }
    this.isUserPaused = false;
    this.playSlide(slide, options || { keepOutgoing: true });
  };

  CustomShorts.prototype.stopPlaying = function () {
    if (this._handoffTimer) {
      clearTimeout(this._handoffTimer);
      this._handoffTimer = null;
    }
    if (this._handoffSafetyTimer) {
      clearTimeout(this._handoffSafetyTimer);
      this._handoffSafetyTimer = null;
    }
    if (this.outgoingSlide) {
      this.teardownSlide(this.outgoingSlide);
      this.outgoingSlide = null;
    }
    if (this.playingSlide) {
      this.teardownSlide(this.playingSlide);
    }
    this.isVisibilityPaused = false;
    this.isUserPaused = false;
    // Keep this.isMuted so mute preference survives pause/resume and slide changes.
  };

  CustomShorts.prototype.toggleMute = function (muteBtn) {
    if (!this.playingSlide) return;
    var iframe = this.playingSlide.querySelector('[data-cs-iframe]');
    if (!iframe) return;

    this.isMuted = !this.isMuted;
    this.applyMuteState(iframe);
    if (muteBtn) {
      muteBtn.setAttribute('aria-pressed', this.isMuted ? 'true' : 'false');
      muteBtn.setAttribute('aria-label', this.isMuted ? 'Unmute' : 'Mute');
    } else {
      this.syncMuteButton(this.playingSlide);
    }
  };

  CustomShorts.prototype.getScrollMetrics = function () {
    if (!this.track) return { maxScroll: 0, scrollLeft: 0 };
    return {
      maxScroll: Math.max(0, this.track.scrollWidth - this.track.clientWidth),
      scrollLeft: this.track.scrollLeft
    };
  };

  CustomShorts.prototype.getScrollTargetForIndex = function (index) {
    if (!this.track || !this.slides.length) return 0;
    var slide = this.slides[index];
    if (!slide) return 0;

    var metrics = this.getScrollMetrics();
    // Always center the active slide (infinite loop has no true edges).
    var target = slide.offsetLeft - (this.track.clientWidth - slide.offsetWidth) / 2;
    return Math.max(0, Math.min(metrics.maxScroll, target));
  };

  CustomShorts.prototype.normalizeLoopIndex = function (index) {
    if (!this.loopEnabled || !this.realCount) return index;
    var start = this.realCount;
    var end = this.realCount * 2 - 1;
    if (index >= start && index <= end) return index;
    var real = ((index % this.realCount) + this.realCount) % this.realCount;
    return start + real;
  };

  CustomShorts.prototype.jumpToEquivalentIfNeeded = function () {
    if (!this.loopEnabled || !this.track || !this.realCount) return false;

    var start = this.realCount;
    var end = this.realCount * 2 - 1;
    if (this.activeIndex >= start && this.activeIndex <= end) return false;

    var next = this.normalizeLoopIndex(this.activeIndex);
    if (next === this.activeIndex) return false;

    // Drop players on the old clone so we don't leave a ghost thumbnail/video behind.
    if (this.playingSlide) this.teardownSlide(this.playingSlide);
    if (this.outgoingSlide) {
      this.teardownSlide(this.outgoingSlide);
      this.outgoingSlide = null;
    }

    this.activeIndex = next;
    this.applyActiveClass(next);
    this.track.scrollLeft = this.getScrollTargetForIndex(next);
    return true;
  };

  CustomShorts.prototype.applyActiveClass = function (index) {
    this.slides.forEach(function (slide, i) {
      slide.classList.toggle('is-active', i === index);
    });
  };

  CustomShorts.prototype.centerInitial = function () {
    if (!this.slides.length) return;
    var self = this;
    this.syncLayoutWidths();
    this.refreshInView();
    this.goTo(this.activeIndex, true);

    // Page-load race: IO / layout may settle after the first play attempt.
    var retries = [0, 80, 250];
    retries.forEach(function (delay) {
      setTimeout(function () {
        if (!self.slides || !self.slides.length) return;
        self.refreshInView();
        if (!self.canPlay()) return;
        self.ensureActivePlaying({ keepOutgoing: false });
      }, delay);
    });
  };

  CustomShorts.prototype.step = function (direction) {
    if (!this.slides.length) return;

    if (this.loopEnabled) {
      this.goTo(this.activeIndex + direction);
      return;
    }

    this.goTo(Math.max(0, Math.min(this.slides.length - 1, this.activeIndex + direction)));
  };

  CustomShorts.prototype.easeInOutCubic = function (t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };

  CustomShorts.prototype.setSlideWidth = function (slide, width) {
    if (!slide || !isFinite(width) || width <= 0) return;
    var value = width.toFixed(2) + 'px';
    slide.style.flex = '0 0 ' + value;
    slide.style.flexBasis = value;
    slide.style.width = value;
    slide.style.minWidth = value;
    slide.style.maxWidth = value;
  };

  CustomShorts.prototype.clearSlideSizeOverrides = function () {
    if (!this.slides || !this.slides.length) return;
    this.slides.forEach(function (slide) {
      slide.style.flex = '';
      slide.style.flexBasis = '';
      slide.style.width = '';
      slide.style.minWidth = '';
      slide.style.maxWidth = '';
    });
  };

  CustomShorts.prototype.syncLayoutWidths = function () {
    if (!this.track || !this.root) return;

    var desktop = window.matchMedia('(min-width: 990px)').matches;
    var single = window.matchMedia('(max-width: 474px)').matches;
    var gap;
    var gapCount;
    var parts;

    if (desktop) {
      gap = 32;
      gapCount = 4;
      parts = 107; /* 5 slides */
    } else if (single) {
      gap = 12;
      gapCount = 2;
      parts = 43; /* 0.4*20 + 27 + 0.4*20 — larger center, 0.4 peek each side */
    } else {
      gap = window.matchMedia('(min-width: 769px)').matches ? 25 : 16;
      gapCount = 2;
      parts = 67; /* 3 slides */
    }

    var trackWidth = this.track.clientWidth || this.track.getBoundingClientRect().width;
    if (!trackWidth || trackWidth < 1) return;

    var avail = Math.max(0, trackWidth - gap * gapCount);
    var unit = avail / parts;
    var inactive = unit * 20;
    var active = unit * 27;

    this.root.style.setProperty('--cs-gap', gap + 'px');
    this.root.style.setProperty('--cs-slide-w', inactive.toFixed(2) + 'px');
    this.root.style.setProperty('--cs-slide-w-active', active.toFixed(2) + 'px');
    this._wInactive = inactive;
    this._wActive = active;
  };

  CustomShorts.prototype.lockTrackHeight = function () {
    if (!this.track) return;
    var wrap = this.root.querySelector('.custom-shorts__slider-wrap');
    var height = Math.ceil(this.track.getBoundingClientRect().height);
    if (height < 1) return;
    if (!this._lockedTrackMinHeight || height > this._lockedTrackMinHeight) {
      this._lockedTrackMinHeight = height;
    }
    this.track.style.minHeight = this._lockedTrackMinHeight + 'px';
    if (wrap) wrap.style.minHeight = this._lockedTrackMinHeight + 'px';
  };

  CustomShorts.prototype.cacheSlideWidths = function () {
    if (!this.slides || !this.slides.length) return;
    this.syncLayoutWidths();
    var active = this.slides[this.activeIndex];
    if (active) {
      var aw = active.getBoundingClientRect().width;
      if (aw > 0) this._wActive = aw;
    }
    for (var i = 0; i < this.slides.length; i++) {
      if (i === this.activeIndex) continue;
      var iw = this.slides[i].getBoundingClientRect().width;
      if (iw > 0) {
        this._wInactive = iw;
        break;
      }
    }
    this.lockTrackHeight();
  };

  CustomShorts.prototype.cancelTransition = function () {
    if (this._scrollRaf) {
      cancelAnimationFrame(this._scrollRaf);
      this._scrollRaf = null;
    }
    if (this._scrollLockTimer) {
      clearTimeout(this._scrollLockTimer);
      this._scrollLockTimer = null;
    }
  };

  /**
   * Proper carousel move: outgoing shrinks, incoming grows, scroll eases to center.
   * Section min-height stays fixed so the page does not jump.
   * Safe to re-enter while animating (retargets from current widths).
   */
  CustomShorts.prototype.animateCarousel = function (fromIndex, toIndex, duration, done) {
    var self = this;
    var track = this.track;
    if (!track) {
      if (done) done();
      return;
    }

    var fromSlide = this.slides[fromIndex];
    var toSlide = this.slides[toIndex];

    if (!this._wActive || !this._wInactive) {
      this.applyActiveClass(this.activeIndex);
      void track.offsetWidth;
      this.cacheSlideWidths();
    }

    var wActive = this._wActive || 227.8125;
    var wInactive = this._wInactive || 168.75;
    var canMorph = Math.abs(wActive - wInactive) > 1;

    var fromStart = fromSlide ? fromSlide.getBoundingClientRect().width : wInactive;
    var toStart = toSlide ? toSlide.getBoundingClientRect().width : wInactive;

    if (canMorph) {
      this.slides.forEach(function (slide, i) {
        if (i === fromIndex || i === toIndex) return;
        self.setSlideWidth(slide, wInactive);
      });
      if (fromSlide) this.setSlideWidth(fromSlide, fromStart);
      if (toSlide) this.setSlideWidth(toSlide, toStart);
    }

    this.applyActiveClass(toIndex);
    void track.offsetWidth;
    this.lockTrackHeight();

    var startScroll = track.scrollLeft;
    var startTime = null;
    duration = duration || 700;

    if (this._scrollRaf) {
      cancelAnimationFrame(this._scrollRaf);
      this._scrollRaf = null;
    }

    function frame(ts) {
      if (!startTime) startTime = ts;
      var progress = Math.min(1, (ts - startTime) / duration);
      var eased = self.easeInOutCubic(progress);

      if (canMorph && fromSlide && toSlide && fromIndex !== toIndex) {
        self.setSlideWidth(fromSlide, fromStart + (wInactive - fromStart) * eased);
        self.setSlideWidth(toSlide, toStart + (wActive - toStart) * eased);
      }

      var liveTarget = self.getScrollTargetForIndex(toIndex);
      track.scrollLeft = startScroll + (liveTarget - startScroll) * eased;

      if (progress < 1) {
        self._scrollRaf = requestAnimationFrame(frame);
      } else {
        self._scrollRaf = null;
        self.applyActiveClass(toIndex);
        self.clearSlideSizeOverrides();
        void track.offsetWidth;
        self.cacheSlideWidths();
        track.scrollLeft = self.getScrollTargetForIndex(toIndex);
        if (done) done();
      }
    }

    this._scrollRaf = requestAnimationFrame(frame);
  };

  CustomShorts.prototype.goTo = function (index, instant) {
    if (!this.track || !this.slides.length) return;

    var next = index;
    if (!this.loopEnabled) {
      next = Math.max(0, Math.min(this.slides.length - 1, index));
    } else {
      next = Math.max(0, Math.min(this.slides.length - 1, index));
    }

    if (
      next === this.activeIndex &&
      !instant &&
      !this.isAnimating &&
      Math.abs(this.track.scrollLeft - this.getScrollTargetForIndex(next)) < 2
    ) {
      this.ensureActivePlaying({ keepOutgoing: true });
      this.updateNavState();
      return;
    }

    var prev = this.activeIndex;
    var self = this;
    var token = (this._animToken = (this._animToken || 0) + 1);

    this.cancelTransition();
    if (this._handoffTimer) {
      clearTimeout(this._handoffTimer);
      this._handoffTimer = null;
    }

    this.activeIndex = next;
    this.updateNavState();

    if (instant) {
      this.clearSlideSizeOverrides();
      this.applyActiveClass(next);
      void this.track.offsetWidth;
      this.cacheSlideWidths();
      this.track.scrollLeft = this.getScrollTargetForIndex(next);
      this.isAnimating = false;
      this.track.classList.remove('is-animating');
      this.jumpToEquivalentIfNeeded();
      this.ensureActivePlaying({ keepOutgoing: false });
      return;
    }

    this.isAnimating = true;
    this._holdActiveUntil = Date.now() + 1000;
    this.track.classList.add('is-animating');

    // Start warming/playing the incoming video immediately so it is ready as the slide settles.
    this.ensureActivePlaying({ keepOutgoing: true });

    this.animateCarousel(prev, next, 700, function () {
      if (token !== self._animToken) return;
      self.finishProgrammaticScroll();
    });

    // Backup play kick if the first attempt raced visibility/layout.
    this._handoffTimer = setTimeout(function () {
      self._handoffTimer = null;
      if (token !== self._animToken) return;
      self.ensureActivePlaying({ keepOutgoing: true });
    }, 120);

    this._scrollLockTimer = setTimeout(function () {
      if (token !== self._animToken) return;
      self.finishProgrammaticScroll();
    }, 1000);
  };

  CustomShorts.prototype.finishProgrammaticScroll = function () {
    if (!this.track) return;
    if (!this.isAnimating && !this._scrollRaf) return;

    this.cancelTransition();

    this.clearSlideSizeOverrides();
    this.applyActiveClass(this.activeIndex);
    void this.track.offsetWidth;
    this.cacheSlideWidths();

    var target = this.getScrollTargetForIndex(this.activeIndex);
    if (Math.abs(this.track.scrollLeft - target) > 2) {
      this.track.scrollLeft = target;
    }

    this.jumpToEquivalentIfNeeded();
    this.cacheSlideWidths();

    this.isAnimating = false;
    this._holdActiveUntil = Date.now() + 200;
    this.track.classList.remove('is-animating');
    this.applyActiveClass(this.activeIndex);
    this.ensureActivePlaying({ keepOutgoing: true });
    this.updateNavState();
  };

  CustomShorts.prototype.isTouchDevice = function () {
    try {
      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) return true;
    } catch (e) {}
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  };

  /* Firefox/Safari keep drawing YT chrome with controls=0 — use light vertical crop only. */
  CustomShorts.prototype.needsChromeCrop = function () {
    var ua = navigator.userAgent || '';
    if (/firefox|fxios/i.test(ua)) return true;
    if (/safari/i.test(ua) && !/chrome|chromium|crios|android|edg/i.test(ua)) return true;
    return false;
  };

  CustomShorts.prototype.onTouchStart = function (event) {
    if (!this.track || !event.touches || !event.touches.length) return;
    if (this.isAnimating) return;

    var touch = event.touches[0];
    this._touch = {
      startX: touch.clientX,
      startY: touch.clientY,
      lastX: touch.clientX,
      index: this.activeIndex,
      scroll: this.track.scrollLeft,
      axis: null,
      dragged: false
    };
  };

  CustomShorts.prototype.onTouchMove = function (event) {
    var state = this._touch;
    if (!state || !this.track || !event.touches || !event.touches.length) return;

    var touch = event.touches[0];
    var dx = touch.clientX - state.startX;
    var dy = touch.clientY - state.startY;

    if (!state.axis) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      state.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }

    // Vertical gesture — let the page scroll normally.
    if (state.axis === 'y') return;

    event.preventDefault();
    state.dragged = true;
    state.lastX = touch.clientX;

    // Follow the finger, but never more than one slide width.
    var maxDrag = Math.max(80, (this._wActive || this._wInactive || 160) * 0.9);
    var drag = Math.max(-maxDrag, Math.min(maxDrag, -dx));
    this.track.scrollLeft = state.scroll + drag;
  };

  CustomShorts.prototype.onTouchEnd = function () {
    var state = this._touch;
    this._touch = null;
    if (!state || !this.track) return;
    if (state.axis === 'y' || !state.dragged) return;

    var dx = state.lastX - state.startX;
    var threshold = Math.max(36, (this._wInactive || this._wActive || 120) * 0.18);

    // One swipe = exactly one slide from where the gesture began.
    if (dx <= -threshold) {
      this.goTo(state.index + 1);
    } else if (dx >= threshold) {
      this.goTo(state.index - 1);
    } else {
      this.goTo(state.index);
    }
  };

  CustomShorts.prototype.onScroll = function () {
    if (this.isAnimating) return;
    if (this._touch) return;
    if (Date.now() < this._holdActiveUntil) return;

    var self = this;
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = requestAnimationFrame(function () {
      self._raf = null;
      self.syncActiveFromScroll();
    });
  };

  CustomShorts.prototype.syncActiveFromScroll = function () {
    if (!this.track || !this.slides.length || this.isAnimating) return;
    if (Date.now() < this._holdActiveUntil) return;

    var trackRect = this.track.getBoundingClientRect();
    var centerX = trackRect.left + trackRect.width / 2;
    var closest = 0;
    var closestDist = Infinity;

    this.slides.forEach(function (slide, index) {
      var rect = slide.getBoundingClientRect();
      var slideCenter = rect.left + rect.width / 2;
      var dist = Math.abs(slideCenter - centerX);
      if (dist < closestDist) {
        closestDist = dist;
        closest = index;
      }
    });

    if (closest !== this.activeIndex) {
      this.activeIndex = closest;
      this.applyActiveClass(closest);
      this.jumpToEquivalentIfNeeded();
      this.ensureActivePlaying({ keepOutgoing: true });
      this.updateNavState();
    } else {
      this.ensureActivePlaying({ keepOutgoing: true });
    }
  };

  CustomShorts.prototype.updateNavState = function () {
    var hasSlides = this.realCount > 1 || this.slides.length > 1;
    var showPrev;
    var showNext;

    if (this.loopEnabled) {
      showPrev = hasSlides;
      showNext = hasSlides;
    } else {
      var atStart = this.activeIndex <= 0;
      var atEnd = !this.slides.length || this.activeIndex >= this.slides.length - 1;
      showPrev = hasSlides && !atStart;
      showNext = hasSlides && !atEnd;
    }

    this.prevBtns.forEach(function (btn) {
      if (btn.hasAttribute('data-cs-side')) {
        btn.hidden = !showPrev;
      } else {
        btn.disabled = !showPrev;
      }
    });

    this.nextBtns.forEach(function (btn) {
      if (btn.hasAttribute('data-cs-side')) {
        btn.hidden = !showNext;
      } else {
        btn.disabled = !showNext;
      }
    });
  };

  CustomShorts.prototype.onResize = function () {
    var self = this;
    requestAnimationFrame(function () {
      self._lockedTrackMinHeight = 0;
      if (self.track) self.track.style.minHeight = '';
      var wrap = self.root.querySelector('.custom-shorts__slider-wrap');
      if (wrap) wrap.style.minHeight = '';
      self.syncLayoutWidths();
      self.goTo(self.activeIndex, true);
    });
  };

  function initAll() {
    document.querySelectorAll('[data-custom-shorts]').forEach(function (root) {
      if (root._customShorts) root._customShorts.destroy();
      root._customShorts = new CustomShorts(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root =
      event.target && event.target.querySelector
        ? event.target.querySelector('[data-custom-shorts]')
        : null;
    if (!root && event.target && event.target.matches && event.target.matches('[data-custom-shorts]')) {
      root = event.target;
    }
    if (!root) return;
    if (root._customShorts) root._customShorts.destroy();
    root._customShorts = new CustomShorts(root);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root =
      event.target && event.target.querySelector
        ? event.target.querySelector('[data-custom-shorts]')
        : null;
    if (root && root._customShorts) {
      root._customShorts.destroy();
      root._customShorts = null;
    }
  });
})();
